import React, { useEffect, useState, useCallback } from 'react';
import { useAppSelector } from '../../store/hooks';
import {
  fetchAdminLLMConfigs,
  createAdminLLMConfig,
  updateAdminLLMConfig,
  deleteAdminLLMConfig,
} from '../../api/admin';
import AdminDataTable, { Column } from '../../components/admin/AdminDataTable';
import AdminPagination from '../../components/admin/AdminPagination';
import AdminConfirmModal from '../../components/admin/AdminConfirmModal';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';

interface LLMConfig {
  id: number;
  provider_name: string;
  model_name: string;
  display_name: string;
  model_type: string;
  api_key: string | null;
  endpoint_url: string;
  max_output_tokens: number;
  temperature: number;
  priority: number;
  use_for: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const EMPTY_FORM = {
  provider_name: '',
  model_name: '',
  display_name: '',
  model_type: 'fast',
  api_key: '',
  endpoint_url: '',
  max_output_tokens: 8192,
  temperature: 0.7,
  priority: 10,
  use_for: 'both',
  is_active: true,
};

const PAGE_SIZE = 20;

const AdminLLMConfig: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);

  const [configs, setConfigs] = useState<LLMConfig[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  // Create modal
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ ...EMPTY_FORM });
  const [creating, setCreating] = useState(false);

  // Edit modal
  const [editConfig, setEditConfig] = useState<LLMConfig | null>(null);
  const [editForm, setEditForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  // Delete modal
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAdminLLMConfigs();
      const all: LLMConfig[] = res.data.results || res.data.configs || res.data || [];
      setTotal(all.length);
      const start = (page - 1) * PAGE_SIZE;
      setConfigs(all.slice(start, start + PAGE_SIZE));
    } catch (err) {
      console.error('Failed to load LLM configs', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!createForm.provider_name || !createForm.model_name || !createForm.endpoint_url) {
      alert('Provider, model name, and endpoint URL are required.');
      return;
    }
    setCreating(true);
    try {
      await createAdminLLMConfig({
        ...createForm,
        api_key: createForm.api_key || null,
      });
      setShowCreate(false);
      setCreateForm({ ...EMPTY_FORM });
      load();
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to create config');
    } finally {
      setCreating(false);
    }
  };

  const handleEdit = async () => {
    if (!editConfig) return;
    setSaving(true);
    try {
      await updateAdminLLMConfig(editConfig.id, {
        ...editForm,
        api_key: editForm.api_key || null,
      });
      setEditConfig(null);
      load();
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to update config');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (deletingId === null) return;
    try {
      await deleteAdminLLMConfig(deletingId);
      setDeletingId(null);
      load();
    } catch (err: any) {
      alert(err?.response?.data?.detail || 'Failed to delete config');
    }
  };

  const openEdit = (cfg: LLMConfig) => {
    setEditConfig(cfg);
    setEditForm({
      provider_name: cfg.provider_name,
      model_name: cfg.model_name,
      display_name: cfg.display_name,
      model_type: cfg.model_type,
      api_key: '', // masked from server, user re-enters if changing
      endpoint_url: cfg.endpoint_url,
      max_output_tokens: cfg.max_output_tokens,
      temperature: cfg.temperature,
      priority: cfg.priority,
      use_for: cfg.use_for,
      is_active: cfg.is_active,
    });
  };

  const inputStyle: React.CSSProperties = {
    backgroundColor: colors.inputBackground,
    border: `1px solid ${colors.borderColor}`,
    color: colors.textColor,
  };

  const columns: Column<LLMConfig>[] = [
    { key: 'display_name', header: 'Display Name', width: '120px' },
    { key: 'provider_name', header: 'Provider', width: '100px' },
    {
      key: 'model_name', header: 'Model', width: '200px',
      render: (r) => (
        <span className="text-xs font-mono" style={{ color: colors.grayScale500 }}>{r.model_name}</span>
      ),
    },
    {
      key: 'model_type', header: 'Type', width: '80px',
      render: (r) => (
        <span
          className="px-2 py-0.5 rounded-full text-xs font-medium"
          style={{
            backgroundColor: r.model_type === 'fast' ? `${colors.green}20` : `${colors.primary}20`,
            color: r.model_type === 'fast' ? colors.green : colors.primary,
          }}
        >
          {r.model_type}
        </span>
      ),
    },
    {
      key: 'use_for', header: 'Use For', width: '100px',
      render: (r) => (
        <span
          className="px-2 py-0.5 rounded-full text-xs"
          style={{ backgroundColor: `${colors.grayScale500}20`, color: colors.grayScale500 }}
        >
          {r.use_for}
        </span>
      ),
    },
    { key: 'priority', header: 'Priority', width: '70px' },
    {
      key: 'is_active', header: 'Active', width: '70px',
      render: (r) => (
        <span style={{ color: r.is_active ? colors.green : colors.alertColor }}>
          {r.is_active ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'actions', header: 'Actions', width: '100px',
      render: (r) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); openEdit(r); }}
            className="p-1.5 rounded-lg transition-colors"
            style={{ backgroundColor: `${colors.primary}15` }}
          >
            {React.createElement(FiEdit2 as any, { size: 14, color: colors.primary })}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setDeletingId(r.id); }}
            className="p-1.5 rounded-lg transition-colors"
            style={{ backgroundColor: `${colors.alertColor}15` }}
          >
            {React.createElement(FiTrash2 as any, { size: 14, color: colors.alertColor })}
          </button>
        </div>
      ),
    },
  ];

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const renderFormFields = (
    form: typeof EMPTY_FORM,
    setForm: React.Dispatch<React.SetStateAction<typeof EMPTY_FORM>>,
  ) => (
    <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium mb-1 block" style={{ color: colors.grayScale500 }}>Display Name *</label>
          <input
            className="w-full px-3 py-2 rounded-xl text-sm outline-none"
            style={inputStyle}
            value={form.display_name}
            onChange={(e) => setForm((p) => ({ ...p, display_name: e.target.value }))}
            placeholder="e.g. Fast"
          />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block" style={{ color: colors.grayScale500 }}>Provider Name *</label>
          <input
            className="w-full px-3 py-2 rounded-xl text-sm outline-none"
            style={inputStyle}
            value={form.provider_name}
            onChange={(e) => setForm((p) => ({ ...p, provider_name: e.target.value }))}
            placeholder="e.g. qwen, simplismart"
          />
        </div>
      </div>

      <div>
        <label className="text-xs font-medium mb-1 block" style={{ color: colors.grayScale500 }}>Model Name *</label>
        <input
          className="w-full px-3 py-2 rounded-xl text-sm outline-none"
          style={inputStyle}
          value={form.model_name}
          onChange={(e) => setForm((p) => ({ ...p, model_name: e.target.value }))}
          placeholder="e.g. Qwen/Qwen3-VL-8B-Instruct"
        />
      </div>

      <div>
        <label className="text-xs font-medium mb-1 block" style={{ color: colors.grayScale500 }}>Endpoint URL *</label>
        <input
          className="w-full px-3 py-2 rounded-xl text-sm outline-none"
          style={inputStyle}
          value={form.endpoint_url}
          onChange={(e) => setForm((p) => ({ ...p, endpoint_url: e.target.value }))}
          placeholder="e.g. http://4.236.19.16/v1"
        />
      </div>

      <div>
        <label className="text-xs font-medium mb-1 block" style={{ color: colors.grayScale500 }}>API Key</label>
        <input
          className="w-full px-3 py-2 rounded-xl text-sm outline-none"
          style={inputStyle}
          type="password"
          value={form.api_key}
          onChange={(e) => setForm((p) => ({ ...p, api_key: e.target.value }))}
          placeholder="Leave blank if self-hosted"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-medium mb-1 block" style={{ color: colors.grayScale500 }}>Model Type</label>
          <select
            className="w-full px-3 py-2 rounded-xl text-sm outline-none"
            style={inputStyle}
            value={form.model_type}
            onChange={(e) => setForm((p) => ({ ...p, model_type: e.target.value }))}
          >
            <option value="fast">fast</option>
            <option value="thinking">thinking</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block" style={{ color: colors.grayScale500 }}>Use For</label>
          <select
            className="w-full px-3 py-2 rounded-xl text-sm outline-none"
            style={inputStyle}
            value={form.use_for}
            onChange={(e) => setForm((p) => ({ ...p, use_for: e.target.value }))}
          >
            <option value="both">both</option>
            <option value="copilot">copilot</option>
            <option value="extract_summary">extract_summary</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="text-xs font-medium mb-1 block" style={{ color: colors.grayScale500 }}>Priority</label>
          <input
            className="w-full px-3 py-2 rounded-xl text-sm outline-none"
            style={inputStyle}
            type="number"
            value={form.priority}
            onChange={(e) => setForm((p) => ({ ...p, priority: parseInt(e.target.value) || 0 }))}
          />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block" style={{ color: colors.grayScale500 }}>Max Tokens</label>
          <input
            className="w-full px-3 py-2 rounded-xl text-sm outline-none"
            style={inputStyle}
            type="number"
            value={form.max_output_tokens}
            onChange={(e) => setForm((p) => ({ ...p, max_output_tokens: parseInt(e.target.value) || 8192 }))}
          />
        </div>
        <div>
          <label className="text-xs font-medium mb-1 block" style={{ color: colors.grayScale500 }}>Temperature</label>
          <input
            className="w-full px-3 py-2 rounded-xl text-sm outline-none"
            style={inputStyle}
            type="number"
            step="0.1"
            min="0"
            max="2"
            value={form.temperature}
            onChange={(e) => setForm((p) => ({ ...p, temperature: parseFloat(e.target.value) || 0.7 }))}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={form.is_active}
          onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
          className="rounded"
        />
        <label className="text-sm" style={{ color: colors.textColor }}>Active</label>
      </div>
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold" style={{ color: colors.textColor }}>LLM Configurations</h1>
          <p className="text-sm mt-1" style={{ color: colors.grayScale500 }}>
            Manage LLM providers, models, and settings for copilot and extract_summary flows.
          </p>
        </div>
        <button
          onClick={() => { setCreateForm({ ...EMPTY_FORM }); setShowCreate(true); }}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium text-white"
          style={{ backgroundColor: colors.primary }}
        >
          {React.createElement(FiPlus as any, { size: 16 })}
          Add Config
        </button>
      </div>

      {/* Table */}
      <AdminDataTable columns={columns} data={configs} loading={loading} emptyMessage="No LLM configs found" />
      <AdminPagination page={page} totalPages={totalPages} onPageChange={setPage} />

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] p-4" style={{ backgroundColor: colors.modalBackground }}>
          <div
            className="rounded-2xl p-6 w-full max-w-lg"
            style={{ backgroundColor: colors.cardBackground || colors.backgroundColor, boxShadow: colors.shadowLarge }}
          >
            <h3 className="text-lg font-bold mb-4" style={{ color: colors.textColor }}>Add LLM Config</h3>
            {renderFormFields(createForm, setCreateForm)}
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setShowCreate(false)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-medium"
                style={{ borderColor: colors.borderColor, color: colors.textColor }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: colors.primary }}
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editConfig && (
        <div className="fixed inset-0 flex items-center justify-center z-[100] p-4" style={{ backgroundColor: colors.modalBackground }}>
          <div
            className="rounded-2xl p-6 w-full max-w-lg"
            style={{ backgroundColor: colors.cardBackground || colors.backgroundColor, boxShadow: colors.shadowLarge }}
          >
            <h3 className="text-lg font-bold mb-4" style={{ color: colors.textColor }}>
              Edit: {editConfig.display_name}
            </h3>
            {renderFormFields(editForm, setEditForm)}
            <p className="text-xs mt-2" style={{ color: colors.grayScale500 }}>
              Leave API key blank to keep existing value.
            </p>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => setEditConfig(null)}
                className="flex-1 py-2.5 rounded-xl border text-sm font-medium"
                style={{ borderColor: colors.borderColor, color: colors.textColor }}
              >
                Cancel
              </button>
              <button
                onClick={handleEdit}
                disabled={saving}
                className="flex-1 py-2.5 rounded-xl text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: colors.primary }}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {deletingId !== null && (
        <AdminConfirmModal
          title="Delete LLM Config"
          message="Are you sure you want to delete this LLM configuration? This cannot be undone."
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setDeletingId(null)}
          destructive
        />
      )}
    </div>
  );
};

export default AdminLLMConfig;
